/**
 * demoFixtureGWU — DEMO_TO_PRIVATE_MIGRATION_V1
 *
 * Static fixture payload representing a fully-populated demo GWU.
 * Used by the DemoToPrivatePanel to show a real-looking workspace
 * without requiring backend access or file uploads.
 *
 * BOUNDARY:
 * - Fixture data = display-only sample
 * - Fixture data ≠ real GWU, real receipts, real verification
 * - "Clone to My Workspace" creates a new real GWU — it does NOT copy
 *   the fixture data server-side; it opens the guided creation flow
 *   with the fixture's metadata as prefill suggestions only
 */

export interface FixtureArtifact {
  fileName: string;
  fileType: string;
  artifactHash: string;
  uploadedAt: string;
  sizeLabel: string;
}

export interface FixtureTimelineEvent {
  id: string;
  type: 'created' | 'artifact_uploaded' | 'receipt_issued' | 'verified' | 'relationship_linked';
  label: string;
  timestamp: string;
  actor: string;
}

export interface FixtureReceipt {
  receiptId: string;
  type: string;
  issuedAt: string;
  hash: string;
  verified: boolean;
}

export interface FixtureRelationship {
  relationshipId: string;
  targetGwuId: string;
  relationshipType: string;
  createdAt: string;
}

export interface DemoFixtureGWU {
  gwuId: string;
  name: string;
  description: string;
  domain: string;
  artifactType: string;
  status: 'active';
  proofOnly: true;
  createdAt: string;
  artifact: FixtureArtifact;
  timeline: FixtureTimelineEvent[];
  receipts: FixtureReceipt[];
  relationships: FixtureRelationship[];
  continuityBurdens: Array<{ id: string; description: string; priority: 'high' | 'medium' | 'low' }>;
  suggestedNextMoves: Array<{ id: string; label: string; rationale: string }>;
}

export const DEMO_FIXTURE_GWU: DemoFixtureGWU = {
  gwuId: 'demo-gwu-001',
  name: 'Sample Governed Work Unit',
  description: 'A demonstration work unit showing the full governance lifecycle: artifact upload, timeline, receipts, relationships, and verification.',
  domain: 'governance-demo',
  artifactType: 'document',
  status: 'active',
  proofOnly: true,
  createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),

  artifact: {
    fileName: 'sample-governance-artifact.pdf',
    fileType: 'application/pdf',
    artifactHash: 'sha256:demo_a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
    uploadedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    sizeLabel: '142 KB',
  },

  timeline: [
    {
      id: 'evt-001',
      type: 'created',
      label: 'GWU created',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      actor: 'demo-user',
    },
    {
      id: 'evt-002',
      type: 'artifact_uploaded',
      label: 'Artifact uploaded and hashed',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000 + 30000).toISOString(),
      actor: 'demo-user',
    },
    {
      id: 'evt-003',
      type: 'receipt_issued',
      label: 'Governance receipt issued',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      actor: 'trustgate-kernel',
    },
    {
      id: 'evt-004',
      type: 'verified',
      label: 'Receipt verified against artifact hash',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      actor: 'demo-user',
    },
    {
      id: 'evt-005',
      type: 'relationship_linked',
      label: 'Linked to upstream GWU',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      actor: 'demo-user',
    },
  ],

  receipts: [
    {
      receiptId: 'demo-rcpt-001',
      type: 'artifact-upload',
      issuedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      hash: 'sha256:demo_rcpt_a1b2c3d4e5f6',
      verified: true,
    },
    {
      receiptId: 'demo-rcpt-002',
      type: 'governance-review',
      issuedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      hash: 'sha256:demo_rcpt_b2c3d4e5f6a7',
      verified: true,
    },
  ],

  relationships: [
    {
      relationshipId: 'demo-rel-001',
      targetGwuId: 'demo-gwu-upstream-001',
      relationshipType: 'depends-on',
      createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    },
  ],

  continuityBurdens: [
    {
      id: 'burden-001',
      description: 'Preserve artifact hash chain for downstream verification',
      priority: 'high',
    },
    {
      id: 'burden-002',
      description: 'Maintain relationship link to upstream GWU until review completes',
      priority: 'medium',
    },
  ],

  suggestedNextMoves: [
    {
      id: 'move-001',
      label: 'Export governance packet',
      rationale: 'Receipt chain is complete — safe to export for external review',
    },
    {
      id: 'move-002',
      label: 'Record disclosure review',
      rationale: 'No disclosure review recorded yet for this work unit',
    },
    {
      id: 'move-003',
      label: 'Link additional relationships',
      rationale: 'Downstream GWU may depend on this work unit',
    },
  ],
};
